/**
 * AI Assistant component for natural language to SQL, SQL explanation, and optimization.
 * Provides a dialog interface for AI-powered SQL assistance.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  Stack,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { aiApi } from '../services/api';
import { AiResponse } from '../types';

interface AiAssistantProps {
  open: boolean;
  onClose: () => void;
  connectionId: string | null;
  currentSql: string;
  onInsertSql: (sql: string) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-tabpanel-${index}`}
      aria-labelledby={`ai-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AiAssistant: React.FC<AiAssistantProps> = ({
  open,
  onClose,
  connectionId,
  currentSql,
  onInsertSql,
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [naturalLanguageInput, setNaturalLanguageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<AiResponse | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setResponse(null);
    setNaturalLanguageInput('');
  };

  const handleNaturalLanguageToSql = async () => {
    if (!connectionId || !naturalLanguageInput.trim()) {
      setResponse({ content: null, error: 'Please select a connection and enter a natural language query' });
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      const result = await aiApi.naturalLanguageToSql(connectionId, naturalLanguageInput);
      setResponse(result);
    } catch (error: any) {
      setResponse({ content: null, error: error.message || 'Failed to convert natural language to SQL' });
    } finally {
      setLoading(false);
    }
  };

  const handleExplainSql = async () => {
    if (!connectionId || !currentSql.trim()) {
      setResponse({ content: null, error: 'Please select a connection and have SQL in the editor' });
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      const result = await aiApi.explainSql(connectionId, currentSql);
      setResponse(result);
    } catch (error: any) {
      setResponse({ content: null, error: error.message || 'Failed to explain SQL' });
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeSql = async () => {
    if (!connectionId || !currentSql.trim()) {
      setResponse({ content: null, error: 'Please select a connection and have SQL in the editor' });
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      const result = await aiApi.optimizeSql(connectionId, currentSql);
      setResponse(result);
    } catch (error: any) {
      setResponse({ content: null, error: error.message || 'Failed to optimize SQL' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (response?.content) {
      navigator.clipboard.writeText(response.content);
    }
  };

  const handleInsertSql = () => {
    if (response?.content && tabValue === 0) {
      // Only insert SQL for natural language to SQL tab
      onInsertSql(response.content);
      onClose();
    }
  };

  const handleClose = () => {
    setTabValue(0);
    setResponse(null);
    setNaturalLanguageInput('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">AI Query Assistant</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="AI assistant tabs">
            <Tab label="Natural Language → SQL" />
            <Tab label="Explain SQL" />
            <Tab label="Optimize SQL" />
          </Tabs>

          {/* Natural Language to SQL Tab */}
          <TabPanel value={tabValue} index={0}>
            <Stack spacing={2}>
              <TextField
                label="Natural Language Query"
                placeholder="e.g., Show top 10 customers by revenue"
                multiline
                rows={3}
                fullWidth
                value={naturalLanguageInput}
                onChange={(e) => setNaturalLanguageInput(e.target.value)}
                disabled={loading || !connectionId}
                helperText={!connectionId ? 'Please select a connection first' : 'Describe what you want to query in plain English'}
              />
              <Button
                variant="contained"
                onClick={handleNaturalLanguageToSql}
                disabled={loading || !connectionId || !naturalLanguageInput.trim()}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                Generate SQL
              </Button>
            </Stack>
          </TabPanel>

          {/* Explain SQL Tab */}
          <TabPanel value={tabValue} index={1}>
            <Stack spacing={2}>
              <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {currentSql || '(No SQL in editor)'}
                </Typography>
              </Paper>
              <Button
                variant="contained"
                onClick={handleExplainSql}
                disabled={loading || !connectionId || !currentSql.trim()}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                Explain SQL
              </Button>
            </Stack>
          </TabPanel>

          {/* Optimize SQL Tab */}
          <TabPanel value={tabValue} index={2}>
            <Stack spacing={2}>
              <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {currentSql || '(No SQL in editor)'}
                </Typography>
              </Paper>
              <Button
                variant="contained"
                onClick={handleOptimizeSql}
                disabled={loading || !connectionId || !currentSql.trim()}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                Optimize SQL
              </Button>
            </Stack>
          </TabPanel>

          {/* Response Display */}
          {response && (
            <Box sx={{ mt: 3 }}>
              {response.error ? (
                <Alert severity="error">{response.error}</Alert>
              ) : (
                <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">Response:</Typography>
                    <IconButton size="small" onClick={handleCopyToClipboard} title="Copy to clipboard">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: tabValue === 0 ? 'monospace' : 'inherit',
                    }}
                  >
                    {response.content}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {tabValue === 0 && response?.content && !response.error && (
          <Button variant="contained" onClick={handleInsertSql}>
            Insert SQL into Editor
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AiAssistant;







